import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  Flex,
  Button,
  LoadingButton,
  PasswordField,
  Loading,
  Checkbox,
  DropDown,
} from '@elementary/common-ui/components';
import { AI_BYOK_SETTINGS } from '@elementary/common-ui/constants/queryKeys';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import {
  fetchByokSettings,
  fetchByokModels,
} from '@elementary/common-ui/services/api';
import { Typography, InputLabel, FormControlLabel } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as settingsApi from 'shared/services/settingsApi';
import { ByokProvider, SetByokSettingsApi } from 'shared/services/settingsApi';
import {
  Form,
  Frame,
  FieldsContainer,
  Description,
} from './settingsCardStyles';

const ModelList = styled(FlexColumn)`
  max-height: 220px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.palette.common.gray8};
  border-radius: 6px;
  padding: 8px 12px;
`;

type ByokForm = {
  provider: ByokProvider;
  apiKey: string;
};

const PROVIDER_OPTIONS: { value: ByokProvider; label: string }[] = [
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
];

const AiByokSection = () => {
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const hasAccountPermission = useAccountPermission();
  const canEdit = hasAccountPermission('EDIT_INTEGRATIONS');

  const { data, isLoading } = useQuery([AI_BYOK_SETTINGS], fetchByokSettings);

  const {
    data: availableData,
    isLoading: modelsLoading,
    isError: modelsError,
  } = useQuery(
    [AI_BYOK_SETTINGS, 'models', data?.provider ?? 'none'],
    fetchByokModels,
    { enabled: !!data?.configured, staleTime: 5 * 60 * 1000 },
  );
  const availableModels = availableData?.models ?? [];

  const { control, register, handleSubmit, reset, watch } = useForm<ByokForm>({
    defaultValues: { provider: 'anthropic', apiKey: '' },
  });
  const selectedProvider = watch('provider');

  const [selectedModels, setSelectedModels] = useState<string[]>([]);

  useEffect(() => {
    reset({ provider: data?.provider || 'anthropic', apiKey: '' });
    setSelectedModels(data?.allowedModels ?? []);
  }, [data, reset]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [AI_BYOK_SETTINGS] });

  const { mutate: saveKey, isLoading: isSavingKey } = useMutation(
    (payload: SetByokSettingsApi) => settingsApi.setByokSettings(payload),
    {
      onSuccess: () => {
        openSnackbar({ message: 'API key saved.' });
        invalidate();
      },
      onError: () =>
        openSnackbar({
          message: 'Failed to save API key.',
          variant: 'error',
        }),
    },
  );

  const { mutate: saveModels, isLoading: isSavingModels } = useMutation(
    (payload: SetByokSettingsApi) => settingsApi.setByokSettings(payload),
    {
      onSuccess: () => {
        openSnackbar({ message: 'Available models updated.' });
        invalidate();
      },
      onError: () =>
        openSnackbar({
          message: 'Failed to update available models.',
          variant: 'error',
        }),
    },
  );

  const { mutate: remove, isLoading: isRemoving } = useMutation(
    () => settingsApi.deleteByokSettings(),
    {
      onSuccess: () => {
        openSnackbar({ message: 'AI provider key removed.' });
        invalidate();
      },
      onError: () =>
        openSnackbar({
          message: 'Failed to remove AI provider key.',
          variant: 'error',
        }),
    },
  );

  if (isLoading) {
    return (
      <Frame>
        <Loading />
      </Frame>
    );
  }

  const toggleModel = (id: string) =>
    setSelectedModels((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    );

  const onSaveKey = (form: ByokForm) => {
    const payload: SetByokSettingsApi = { provider: form.provider };
    if (form.apiKey.trim()) {
      payload.apiKey = form.apiKey.trim();
    }
    saveKey(payload);
  };

  const onSaveModels = () => {
    if (!data?.provider) {
      return;
    }
    saveModels({ provider: data.provider, allowedModels: selectedModels });
  };

  return (
    <Form onSubmit={handleSubmit(onSaveKey)}>
      <Frame>
        <Flex
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <FlexColumn>
            <Typography sx={{ fontWeight: 'bold' }}>
              AI provider key (BYOK)
            </Typography>
            <Description>
              Use your own Anthropic or OpenAI API key for AI agents. When set,
              prompts are sent using your provider account and Elementary spend
              limits no longer apply. Note: your provider may log request
              content under your account.
            </Description>
          </FlexColumn>
          {data?.configured && (
            <Button
              variant="text"
              color="secondary"
              disabled={!canEdit || isRemoving}
              onClick={() => remove()}
            >
              Remove
            </Button>
          )}
        </Flex>
        <FieldsContainer>
          <FlexColumn>
            <InputLabel shrink>Provider</InputLabel>
            <Controller
              name="provider"
              control={control}
              render={({ field }) => (
                <DropDown
                  value={field.value}
                  onChange={field.onChange}
                  options={PROVIDER_OPTIONS}
                  size="large"
                  disabled={!canEdit}
                />
              )}
            />
          </FlexColumn>
          <FlexColumn>
            <InputLabel shrink>API key</InputLabel>
            <PasswordField
              size="large"
              disabled={!canEdit}
              placeholder={
                data?.configured
                  ? `Stored key ending in ••••${data.keyLast4 ?? ''}`
                  : 'Enter your provider API key'
              }
              {...register('apiKey', {
                required:
                  !data?.configured ||
                  (data.provider != null && selectedProvider !== data.provider)
                    ? 'API key is required when changing provider'
                    : false,
              })}
            />
            <Flex sx={{ justifyContent: 'flex-end', marginTop: '12px' }}>
              <LoadingButton
                type="submit"
                loading={isSavingKey}
                variant="outlined"
                disabled={!canEdit}
              >
                Save key
              </LoadingButton>
            </Flex>
          </FlexColumn>
          {data?.configured && (
            <FlexColumn>
              <Flex
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <InputLabel shrink>Models available in chat</InputLabel>
                {availableModels.length > 0 && (
                  <Flex sx={{ gap: '12px' }}>
                    <Button
                      variant="text"
                      color="secondary"
                      disabled={!canEdit}
                      onClick={() => setSelectedModels(availableModels)}
                    >
                      Select all
                    </Button>
                    <Button
                      variant="text"
                      color="secondary"
                      disabled={!canEdit}
                      onClick={() => setSelectedModels([])}
                    >
                      Clear
                    </Button>
                  </Flex>
                )}
              </Flex>
              <Description>
                Choose which of your provider&apos;s models appear in the chat
                model selector. Select none to offer all available models.
              </Description>
              {modelsLoading ? (
                <Loading />
              ) : modelsError ? (
                <Typography sx={{ fontSize: 13 }}>
                  Couldn&apos;t load models from the provider — check the API
                  key.
                </Typography>
              ) : (
                <>
                  <ModelList>
                    {availableModels.map((modelId) => (
                      <FormControlLabel
                        key={modelId}
                        control={
                          <Checkbox
                            checked={selectedModels.includes(modelId)}
                            disabled={!canEdit}
                            onChange={() => toggleModel(modelId)}
                          />
                        }
                        label={modelId}
                      />
                    ))}
                  </ModelList>
                  <Flex sx={{ justifyContent: 'flex-end', marginTop: '12px' }}>
                    <LoadingButton
                      type="button"
                      loading={isSavingModels}
                      variant="outlined"
                      disabled={!canEdit}
                      onClick={onSaveModels}
                    >
                      Save models
                    </LoadingButton>
                  </Flex>
                </>
              )}
            </FlexColumn>
          )}
        </FieldsContainer>
      </Frame>
    </Form>
  );
};

export default AiByokSection;
